import type { ComponentPropsWithoutRef, ElementType, MouseEvent, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import type { PolymorphicProps, PolymorphicRef } from './polymorphic'

interface ButtonOwnProps {
  /**
   * `'fd-outline-neutral' | 'fd-outline-secondary' | 'fd-filled-neutral'` are
   * EP-114 additions (the NEW onboarding/customers design system, D-0b) —
   * border/text treatments the Figma needs that no DaisyUI `btn-*` color maps
   * to (exit-wizard button, desktop Back button + step-2 quick-add chips,
   * mobile Back button). Built from the `--color-fd-*` tokens in
   * `web/src/index.css`, never a raw hex. Strictly ADDITIVE: every existing
   * literal below renders byte-identical classes to before — do not redefine
   * one to match the new system, since existing variants are consumed
   * app-wide by pages still on the current standard. See
   * `docs/ep-114-onboarding-ui-update.md` §6 (G-1) / §8 (D-0b).
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'accent'
    | 'ghost'
    | 'link'
    | 'outline'
    | 'error'
    | 'success'
    | 'warning'
    | 'default'
    | 'info'
    | 'fd-outline-neutral'
    | 'fd-outline-secondary'
    | 'fd-filled-neutral'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /**
   * Renders `btn-circle` / `btn-square`. Icon-only buttons using `shape`
   * MUST get a caller-supplied `aria-label` — Button does not enforce this.
   */
  shape?: 'circle' | 'square'
  /**
   * Appends `btn-outline` alongside any color variant (e.g. variant="error"
   * + outline renders `btn-error btn-outline`). The legacy `variant="outline"`
   * literal is untouched and keeps rendering identically to before.
   * NOTE: outline composed with variant "ghost" | "link" | "default" has no
   * visual effect — DaisyUI's `btn-outline` needs a color to outline. This
   * is by design, not a bug. The same applies to the `fd-*` variants above —
   * they're already `btn-ghost`-based flat treatments, so `btn-outline` has
   * nothing to color.
   */
  outline?: boolean
  loading?: boolean
  block?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  /**
   * Declared here, not only inherited from the native button, so the
   * non-native path the component already implements (`as={Link}`,
   * `as="span"`: aria-disabled, no pointer events, tabIndex -1) type-checks
   * for elements that have no `disabled` of their own.
   */
  disabled?: boolean
}

export type ButtonProps<C extends ElementType = 'button'> = PolymorphicProps<C, ButtonOwnProps>

const variantClasses = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  accent: 'btn-accent',
  ghost: 'btn-ghost',
  link: 'btn-link',
  outline: 'btn-outline',
  error: 'btn-error',
  success: 'btn-success',
  warning: 'btn-warning',
  default: '',
  info: 'btn-info',
  // EP-114 (new design system, D-0b) — additive only; none of the entries
  // above are touched. `btn-ghost` gives a transparent background + no
  // border for free (matching the app's existing "flat color" variants),
  // then the fd-* border/text/bg utilities from web/src/index.css layer on
  // top. `fd-outline-*` need an explicit `border` width since ghost has none;
  // `fd-filled-neutral` stays borderless (mobile Back button, no border in
  // the Figma).
  'fd-outline-neutral': 'btn-ghost border border-fd-blue-active text-fd-grey-darker',
  'fd-outline-secondary': 'btn-ghost border border-base-content text-base-content',
  'fd-filled-neutral': 'btn-ghost bg-fd-grey-light text-base-content',
}

const sizeClasses = {
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: '',
  lg: 'btn-lg',
}

const shapeClasses = {
  circle: 'btn-circle',
  square: 'btn-square',
}

export function Button<C extends ElementType = 'button'>(props: ButtonProps<C>) {
  // Destructuring directly off the generic `ButtonProps<C>` trips TS on
  // indexed-access defaults (`className = ''` etc.) since it can't prove the
  // default is assignable for an arbitrary `C` — narrow to a concrete shape
  // first, matching the same "necessary" polymorphic-component workaround
  // documented on PolymorphicRef/PolymorphicProps.
  const {
    as,
    className = '',
    variant = 'primary',
    size = 'md',
    shape,
    outline,
    loading,
    block,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ref,
    ...rest
  } = props as ButtonOwnProps & {
    as?: ElementType
    ref?: PolymorphicRef<C>
    className?: string
    children?: ReactNode
    disabled?: boolean
  } & Record<string, unknown>

  const Component = (as ?? 'button') as ElementType
  const isNative = Component === 'button'
  const isDisabled = Boolean(disabled) || Boolean(loading)
  // Non-native disabled (e.g. as={Link}) has no native `disabled` semantics:
  // aria-disabled alone doesn't stop a focused <a> firing "click" on Enter,
  // so we also style it inert and intercept the click below.
  const nonNativeDisabled = !isNative && isDisabled

  // `rest` is generic over C's native props, which TS can't statically prove
  // contains `onClick` for an arbitrary ElementType — narrow it locally so
  // we can wrap it without widening the component's public prop types.
  const nativeRest = rest as ComponentPropsWithoutRef<'a'> & Record<string, unknown>
  const userOnClick = nativeRest.onClick as ((event: MouseEvent) => void) | undefined

  const handleClick = (event: MouseEvent) => {
    if (nonNativeDisabled) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    userOnClick?.(event)
  }

  return (
    <Component
      {...nativeRest}
      ref={ref as PolymorphicRef<C>}
      className={`btn ${variantClasses[variant]} ${outline ? 'btn-outline' : ''} ${shape ? shapeClasses[shape] : ''} ${sizeClasses[size]} ${block ? 'btn-block' : ''} ${nonNativeDisabled ? 'pointer-events-none opacity-50' : ''} ${className}`}
      disabled={isNative ? isDisabled : undefined}
      aria-disabled={nonNativeDisabled ? true : undefined}
      tabIndex={nonNativeDisabled ? -1 : undefined}
      onClick={handleClick}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {!loading && leftIcon}
      {children}
      {!loading && rightIcon}
    </Component>
  )
}

Button.displayName = 'Button'
